import produce from "immer";
import { createAction, createAsyncAction } from "typesafe-actions";
import { ActionType, createReducer } from "typesafe-actions";
import { APICallStatus, VisitorParkingRequest, VisitorParkingRequestStatus } from "../../../Types/UnitFeatureTypes";


interface VisitorParkingRequestState {
  request: {
    loading: boolean;
    success: boolean;
    statusUpdateApiStatus: APICallStatus;
    approvalApiStatus: APICallStatus;
    error: string | null;
    visitorParkingId: string;
  };
}

export enum Types {
  CREATE_VISITOR_PARKING_REQUEST = 'CREATE_VISITOR_PARKING_REQUEST',
  CREATE_VISITOR_PARKING_SUCCESS = 'CREATE_VISITOR_PARKING_SUCCESS',
  CREATE_VISITOR_PARKING_FAILURE = 'CREATE_VISITOR_PARKING_FAILURE',
  UPDATE_VISITOR_PARKING_REQUEST = 'UPDATE_VISITOR_PARKING_REQUEST',
  UPDATE_VISITOR_PARKING_SUCCESS = 'UPDATE_VISITOR_PARKING_SUCCESS',
  UPDATE_VISITOR_PARKING_FAILURE = 'UPDATE_VISITOR_PARKING_FAILURE',
  APPROVE_VISITOR_PARKING_REQUEST = 'APPROVE_VISITOR_PARKING_REQUEST',
  APPROVE_VISITOR_PARKING_SUCCESS = 'APPROVE_VISITOR_PARKING_SUCCESS',
  APPROVE_VISITOR_PARKING_FAILURE = 'APPROVE_VISITOR_PARKING_FAILURE',
  REJECT_VISITOR_PARKING_REQUEST = 'REJECT_VISITOR_PARKING_REQUEST',
  STATUS_UPDATE_VISITOR_PARKING_REQUEST = 'STATUS_UPDATE_VISITOR_PARKING_REQUEST',
  STATUS_UPDATE_VISITOR_PARKING_SUCCESS = 'STATUS_UPDATE_VISITOR_PARKING_SUCCESS',
  STATUS_UPDATE_VISITOR_PARKING_FAILURE = 'STATUS_UPDATE_VISITOR_PARKING_FAILURE',
  RESET_STATUS_REQUEST = 'RESET_STATUS_REQUEST',
}

const createVisitorParkingRequestAction = createAsyncAction(
  Types.CREATE_VISITOR_PARKING_REQUEST,
  Types.CREATE_VISITOR_PARKING_SUCCESS,
  Types.CREATE_VISITOR_PARKING_FAILURE,
)
<
  { visitorParkingData: VisitorParkingRequest, },
  { id: string },
  { message: string }
>();

const updateVisitorParkingRequestAction = createAsyncAction(
  Types.UPDATE_VISITOR_PARKING_REQUEST,
  Types.UPDATE_VISITOR_PARKING_SUCCESS,
  Types.UPDATE_VISITOR_PARKING_FAILURE,
)
<
  { visitorParkingData: VisitorParkingRequest },
  void,
  { message: string }
>();

const approveVisitorParkingRequestAction = createAsyncAction(
  Types.APPROVE_VISITOR_PARKING_REQUEST,
  Types.APPROVE_VISITOR_PARKING_SUCCESS,
  Types.APPROVE_VISITOR_PARKING_FAILURE,
)
<
  { approval_date: Date, approved_by: number, id: number },
  void,
  { message: string }
>();

const statusUpdateVisitorParkingRequestAction = createAsyncAction(
  Types.STATUS_UPDATE_VISITOR_PARKING_REQUEST,
  Types.STATUS_UPDATE_VISITOR_PARKING_SUCCESS,
  Types.STATUS_UPDATE_VISITOR_PARKING_FAILURE,
)
<
  { id: number, status: VisitorParkingRequestStatus },
  void,
  { message: string }
>();

const resetStatusRequestAction = createAction(Types.RESET_STATUS_REQUEST)();

export const Actions = { createVisitorParkingRequestAction, updateVisitorParkingRequestAction, approveVisitorParkingRequestAction, statusUpdateVisitorParkingRequestAction, resetStatusRequestAction };

export const initialState: VisitorParkingRequestState = {
  request: {
    loading: false,
    error: null as string | null,
    statusUpdateApiStatus: APICallStatus.PENDING,
    approvalApiStatus: APICallStatus.PENDING,
    success: false,
    visitorParkingId: '',
  }
};

export type VisitorParkingRequestInfoState = typeof initialState;

const createVisitorParkingRequestRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.success = false;
    draft.request.error = null;

    return draft;
  });

const createVisitorParkingRequestSuccess = (
  state = initialState,
  { payload }: ActionType<typeof Actions.createVisitorParkingRequestAction.success>
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.success = true;
    if (draft.request.success) {
      draft.request.loading = false;
      draft.request.visitorParkingId = payload.id;
    }

    return draft;
  });

const createVisitorParkingRequestFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.createVisitorParkingRequestAction.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.success = false;
    draft.request.error = payload.message;

    return draft;
  });

const updatedVisitorParkingRequestRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.success = false;
    draft.request.error = null;

    return draft;
  });

const updatedVisitorParkingRequestSuccess = (
  state = initialState,
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.success = true;
    if (draft.request.success) {
      draft.request.loading = false;
    }

    return draft;
  });

const updatedVisitorParkingRequestFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.updateVisitorParkingRequestAction.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.success = false;
    draft.request.error = payload.message;

    return draft;
  });

const approveVisitorParkingRequestRequest = (state = initialState) =>
  produce(state, (draft) => {
    draft.request.loading = true;
    draft.request.success = false;
    draft.request.error = null;
    draft.request.approvalApiStatus = APICallStatus.PENDING;
  });

const approveVisitorParkingRequestSuccess = (
  state = initialState,
) =>
  produce(state, (draft) => {
    draft.request.error = null;
    draft.request.success = true;
    draft.request.approvalApiStatus = APICallStatus.SUCCESS;
    if (draft.request.success) {
      draft.request.loading = false;
    }

    return draft;
  });

const approveVisitorParkingRequestFailure = (
  state = initialState,
  { payload }: ActionType<typeof Actions.approveVisitorParkingRequestAction.failure>
) =>
  produce(state, (draft) => {
    draft.request.loading = false;
    draft.request.success = false;
    draft.request.approvalApiStatus = APICallStatus.FAILURE;
    draft.request.error = payload.message;

    return draft;
  });

  const statusUpdateVisitorParkingRequestRequest = (state = initialState) =>
    produce(state, (draft) => {
      draft.request.loading = true;
      draft.request.success = false;
      draft.request.error = null;
      draft.request.statusUpdateApiStatus = APICallStatus.PENDING;
    });

  const statusUpdateVisitorParkingRequestSuccess = (
    state = initialState,
  ) =>
    produce(state, (draft) => {
      draft.request.error = null;
      draft.request.success = true;
      draft.request.statusUpdateApiStatus = APICallStatus.SUCCESS;
      if (draft.request.success) {
        draft.request.loading = false;
      }
  
      return draft;
    });

  const statusUpdateVisitorParkingRequestFailure = (
    state = initialState,
    { payload }: ActionType<typeof Actions.statusUpdateVisitorParkingRequestAction.failure>
  ) =>
    produce(state, (draft) => {
      draft.request.loading = false;
      draft.request.success = false;
      draft.request.statusUpdateApiStatus = APICallStatus.FAILURE;
      draft.request.error = payload.message;
    });

  const resetStatusRequest = (state = initialState) =>
    produce(state, (draft) => {
      draft.request.loading = false;
      draft.request.statusUpdateApiStatus = APICallStatus.PENDING;
      draft.request.approvalApiStatus = APICallStatus.PENDING;
      draft.request.error = null;
      draft.request.success = false;
    });

export default createReducer<typeof initialState, ActionType<typeof Actions>>(
  initialState
)
  .handleAction(Actions.createVisitorParkingRequestAction.request, createVisitorParkingRequestRequest)
  .handleAction(Actions.createVisitorParkingRequestAction.success, createVisitorParkingRequestSuccess)
  .handleAction(Actions.createVisitorParkingRequestAction.failure, createVisitorParkingRequestFailure)
  .handleAction(Actions.updateVisitorParkingRequestAction.request, updatedVisitorParkingRequestRequest)
  .handleAction(Actions.updateVisitorParkingRequestAction.success, updatedVisitorParkingRequestSuccess)
  .handleAction(Actions.updateVisitorParkingRequestAction.failure, updatedVisitorParkingRequestFailure)
  .handleAction(Actions.approveVisitorParkingRequestAction.request, approveVisitorParkingRequestRequest)
  .handleAction(Actions.approveVisitorParkingRequestAction.success, approveVisitorParkingRequestSuccess)
  .handleAction(Actions.approveVisitorParkingRequestAction.failure, approveVisitorParkingRequestFailure)
  .handleAction(Actions.statusUpdateVisitorParkingRequestAction.request, statusUpdateVisitorParkingRequestRequest)
  .handleAction(Actions.statusUpdateVisitorParkingRequestAction.success, statusUpdateVisitorParkingRequestSuccess)
  .handleAction(Actions.statusUpdateVisitorParkingRequestAction.failure, statusUpdateVisitorParkingRequestFailure)
  .handleAction(Actions.resetStatusRequestAction, resetStatusRequest);
  